import { observer } from "mobx-react";
import { DefaultLayout } from "../../../containers";
import { ReactNode, useEffect, useMemo } from "react";
import InvitationRequestPageStore, {
  BatchInvitationRequestPageStore,
  BatchInvitationRequestReady,
  InvitationRequestReady,
} from "../../../stores/InvitationRequestPageStore";
import { Button } from "../../../components-v2/Button";
import { Alert } from "../../../components-v2/Alert";
import DCompanyFooter from "../../../components-v2/DCompanyFooter";
import { portfolioUrl } from "../../../utils/navigation";
import { LoadingIndicator } from "../../../components-v2/LoadingIndicator";
import { Box, Container, VBox } from "../../../components-v2/Box";
import { useRootStore, useTranslation } from "../../../stores/RootStoreContext";
import { FullScreenLayout } from "../../../components-v2/FullScreenLayout";
import { useAppBar } from "../../../appBar/useAppBar";

function InvitationRequestLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      <h2>{t("header.invitation-request")}</h2>
      {children}
    </>
  );
}

export const InvitationRequestView = observer(function ({
  invitationRequest: { data, state },
  onAccept,
  onReject,
}: {
  invitationRequest: InvitationRequestReady;
  onAccept: () => void;
  onReject: () => void;
}) {
  const { t } = useTranslation();
  const history = useRootStore().history;

  return (
    <InvitationRequestLayout>
      <VBox gap={"md"}>
        <p>
          <strong>{data.requestedByName}</strong> ({data.requestedByEmail}){" "}
          {t("invitation-request.has-requested-to-be-invited")} <strong>{data.companyName}</strong>.
        </p>
        {(state === "ready" || state === "accepting" || state === "rejecting") && (
          <Box marginTop gap={"md"}>
            <Button
              variant={"primary"}
              label={t("invitation-request.accept-request")}
              onClick={onAccept}
              loading={state === "accepting"}
            />
            <Button
              label={t("invitation-request.ignore")}
              variant={"secondary"}
              onClick={onReject}
              loading={state === "rejecting"}
            />
          </Box>
        )}
        {state === "accept-success" && (
          <Alert type="success" message={t("invitation-request.accept-success-message")} />
        )}
        {state === "accept-error" && (
          <Alert type="error" message={t("invitation-request.accept-error-message")} />
        )}
        {state === "reject-success" && (
          <Alert type="info" message={t("invitation-request.reject-success-message")} />
        )}
        {state === "reject-error" && (
          <Alert type="error" message={t("invitation-request.reject-error-message")} />
        )}
        {state === "accept-success" && (
          <p>
            <Button
              variant={"primary"}
              label={t("invitation-request.gotoPortfolioButton")}
              onClick={() => history.push(portfolioUrl)}
            />
          </p>
        )}
      </VBox>
      <Box>
        <DCompanyFooter />
      </Box>
    </InvitationRequestLayout>
  );
});

export function InvitationRequestErrorView() {
  const { t } = useTranslation();
  return (
    <InvitationRequestLayout>
      <Alert type="error" message={t("invitation-request.load-error-message")} />
    </InvitationRequestLayout>
  );
}

const InvitationRequestPage = observer(function InvitationRequestPage({
  invitationRequestId,
}: {
  invitationRequestId: string;
}) {
  const rootStore = useRootStore();
  const store = useMemo(
    () => new InvitationRequestPageStore(invitationRequestId, rootStore),
    [invitationRequestId, rootStore],
  );

  useEffect(() => {
    store.fetch();
  }, [store]);

  switch (store.invitationRequest.state) {
    case "loading":
      return <LoadingIndicator fullscreen />;
    case "error":
      return (
        <DefaultLayout maxWidth={"lg"}>
          <InvitationRequestErrorView />
        </DefaultLayout>
      );
    default:
      return (
        <DefaultLayout maxWidth={"lg"}>
          <InvitationRequestView
            invitationRequest={store.invitationRequest}
            onReject={store.reject}
            onAccept={store.accept}
          />
        </DefaultLayout>
      );
  }
});

export const BatchInvitationRequestPage = observer(function BatchInvitationRequestPage({
  batchId,
}: {
  batchId: string;
}) {
  const rootStore = useRootStore();
  const store = useMemo(
    () => new BatchInvitationRequestPageStore(batchId, rootStore),
    [batchId, rootStore],
  );

  useEffect(() => {
    store.fetch();
  }, [store]);

  const appBar = useAppBar();
  switch (store.invitationRequest.state) {
    case "loading":
      return <LoadingIndicator fullscreen />;
    case "error":
      return (
        <DefaultLayout maxWidth={"lg"}>
          <InvitationRequestErrorView />
        </DefaultLayout>
      );
    default:
      return (
        <FullScreenLayout
          appBar={appBar}
          centerPanel={
            <BatchInvitationRequestView
              invitationRequest={store.invitationRequest}
              onReject={store.reject}
              onAccept={store.accept}
            />
          }
        />
      );
  }
});

export const BatchInvitationRequestView = observer(function ({
  invitationRequest: { data, state },
  onAccept,
  onReject,
}: {
  invitationRequest: BatchInvitationRequestReady;
  onAccept: () => void;
  onReject: () => void;
}) {
  const { t } = useTranslation();
  const history = useRootStore().history;

  return (
    <Container maxWidth={"lg"}>
      <InvitationRequestLayout>
        <div>
          <VBox>
            <Box>
              <strong>{data[0].requestedByName}</strong> ({data[0].requestedByEmail}){" "}
              {t("invitation-request.has-requested-to-be-invited-in")}
            </Box>

            <ul>
              {data.map((item) => (
                <li key={item.companyName}>- {item.companyName}</li>
              ))}
            </ul>
          </VBox>
          {(state === "ready" || state === "accepting" || state === "rejecting") && (
            <Box marginTop gap={"md"}>
              <Button
                variant={"primary"}
                label={t("invitation-request.accept-request")}
                onClick={onAccept}
                loading={state === "accepting"}
              />
              <Button
                label={t("invitation-request.ignore")}
                variant={"secondary"}
                onClick={onReject}
                loading={state === "rejecting"}
              />
            </Box>
          )}
          {state === "accept-success" && (
            <Alert type="success" message={t("invitation-request.accept-success-message")} />
          )}
          {state === "accept-error" && (
            <Alert type="error" message={t("invitation-request.accept-error-message")} />
          )}
          {state === "reject-success" && (
            <Alert type="info" message={t("invitation-request.reject-success-message")} />
          )}
          {state === "reject-error" && (
            <Alert type="error" message={t("invitation-request.reject-error-message")} />
          )}
          {state === "accept-success" && (
            <p>
              <Button
                variant={"primary"}
                label={t("invitation-request.gotoPortfolioButton")}
                onClick={() => history.push(portfolioUrl)}
              />
            </p>
          )}
        </div>
        <Box>
          <DCompanyFooter />
        </Box>
      </InvitationRequestLayout>
    </Container>
  );
});

export default InvitationRequestPage;
